import { ApplicationSelectData, ApplicationsMetaData, AuthenticatedContext, User } from '@sec-loans-ui/common/types'
import {
  createWizardMachine,
  shouldHydrateState,
  WizardContext,
  WizardMachineType,
  WizardStep,
} from '@sec-loans-ui/common/wizard/rootWizardMachine'
import { SecLoansAnalyticsService } from '@sec-loans-ui/services/analyticsService'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { getSecLoansOpenAbortModalEvent } from '@sec-loans-ui/wizard/Wizard'
import { SecLoansCreateApplicationResponse } from '@shared/sec-loans'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { getLanguage } from '@ui-common/utils'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { ActorRefFrom, ActorRefFromLogic, SnapshotFrom } from 'xstate'

import { OnCreatedIfpApplicationData } from '../../ifp-loans/types'
import { OnAutomaticOfferData } from '../../loan-offer/types'
import { secLoansExit } from '../../utils/helpers'

export interface RootWizardLogic {
  actor: ActorRefFrom<WizardMachineType>
  analyticsService: SecLoansAnalyticsService
  step: WizardStep
  user?: User
  applications?: ApplicationsMetaData
  authToken?: string
  secLoansEntryContext: WizardContext['secLoansEntryContext']
  ifpLoansEntryContext: WizardContext['ifpLoansEntryContext']
  loanOfferEntryContext: WizardContext['loanOfferEntryContext']
  send: ActorRefFromLogic<WizardMachineType>['send']
  refetchAndGoToCatalog: () => void
  onNewApplicationSelect: () => void
  onApplicationSelect: (applicationSelectData: ApplicationSelectData) => void
  onCreatedSecLoansApplication: (data: SecLoansCreateApplicationResponse) => void
  onCreatedIfpLoansApplication: (data: OnCreatedIfpApplicationData) => void
  onAutomaticOfferResponse: (data: OnAutomaticOfferData) => void
  onAbort: () => void
  showAbortPromptModal: () => void
}

const selectValues = ({
  context: {
    user,
    applications,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    authToken,
    exitUrls,
  },
  children,
  value,
}: SnapshotFrom<WizardMachineType>) => ({
  user,
  applications,
  secLoansEntryContext,
  ifpLoansEntryContext,
  loanOfferEntryContext,
  authToken,
  exitUrls,
  children,
  step: value,
})

/**
 * Not to be used outside RootWizard
 */
export const useWizardLogic = (authenticatedContext?: AuthenticatedContext): RootWizardLogic => {
  const intl = useIntl()

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine(authenticatedContext)
  const actor = usePersistingActor<WizardMachineType>(wizardMachine, shouldHydrateState)
  const { send } = actor

  const {
    step,
    user,
    applications,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    authToken,
    exitUrls,
  } = useSelector(actor, selectValues, shallowEqual)

  const refetchAndGoToCatalog = useCallback(() => {
    send({ type: 'GO_TO_GET_ALL_APPLICATIONS' })
  }, [send])

  const onNewApplicationSelect = useCallback(() => {
    send({ type: 'GO_TO_ASSISTANCE' })
  }, [send])

  const onApplicationSelect = useCallback(
    (applicationSelectData: ApplicationSelectData) => {
      send({ type: 'SELECT_APP', ...applicationSelectData })
    },
    [send],
  )

  const onCreatedSecLoansApplication = useCallback(
    (data: SecLoansCreateApplicationResponse) => {
      send({ type: 'CREATED_SEC_LOANS_APPLICATION', data })
    },
    [send],
  )

  const onCreatedIfpLoansApplication = useCallback(
    (data: OnCreatedIfpApplicationData) => {
      send({ type: 'CREATED_IFP_LOANS_APPLICATION', data })
    },
    [send],
  )

  const onAutomaticOfferResponse = useCallback(
    (data: OnAutomaticOfferData) => {
      send({ type: 'GO_TO_LOAN_OFFER', data })
    },
    [send],
  )

  const onAbort = useCallback(() => {
    send({ type: 'EXIT_WIZARD' })
  }, [send])

  useEffect(() => {
    void (async () => {
      if (step === WizardStep.Aborted) {
        await secLoansExit({ exitUrls, isSuccess: false, language: getLanguage() })
      }
    })()
  }, [step, exitUrls])

  const { analyticsService } = useAnalyticsService()
  const showAbortPromptModal = () => {
    analyticsService.sendModalViewEvent('AbortModal')
    const openEvent: OpenModalEvent = getSecLoansOpenAbortModalEvent({
      onAbort: () => {
        analyticsService.sendCustomEvent('AbortModal', 'ExitButton')
        onAbort()
      },
      analyticsService,
      analyticsCategory: 'AbortModal',
      intl,
    })
    modalSend(openEvent)
  }

  return {
    actor,
    analyticsService,
    step,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    user,
    applications,
    authToken,
    send: actor.send,
    refetchAndGoToCatalog,
    onNewApplicationSelect,
    onApplicationSelect,
    onCreatedSecLoansApplication,
    onCreatedIfpLoansApplication,
    onAutomaticOfferResponse,
    onAbort,
    showAbortPromptModal,
  }
}
